import React, { useRef, useEffect, useContext } from 'react';
import * as THREE from 'three';
import { ThemeContext } from '../../App'; // Make sure path is correct

interface CardBackgroundProps {
  color?: string;
}

const CardBackground: React.FC<CardBackgroundProps> = ({ color = '#1a1a1a' }) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const { theme } = useContext(ThemeContext);
  
  useEffect(() => {
    if (!mountRef.current) return;
    
    const currentMount = mountRef.current;
    
    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, 1.5, 0.1, 1000);
    
    const width = currentMount.clientWidth || 300;
    const height = currentMount.clientHeight || 200;
    
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true,
      antialias: true 
    });
    
    renderer.setSize(width, height);
    currentMount.appendChild(renderer.domElement);
    
    // Parse the color
    const mainColor = new THREE.Color(color);
    const darkerColor = new THREE.Color(color).multiplyScalar(0.6);
    
    // Create a minimalist mountain backdrop matching our main scene
    const createMiniMountain = () => {
      const shape = new THREE.Shape();
      const width = 8;
      const segments = 6;
      
      // Start at bottom left
      shape.moveTo(-width/2, -2);
      
      // Create mountain peaks
      for (let i = 0; i <= segments; i++) {
        const x = -width/2 + (width * i / segments);
        // Create randomized mountain peaks that are more gentle
        const y = Math.sin(i/segments * Math.PI) * 0.8 + (Math.random() * 0.4);
        shape.lineTo(x, y);
      }
      
      // Close shape
      shape.lineTo(width/2, -2);
      
      const geometry = new THREE.ShapeGeometry(shape);
      const material = new THREE.MeshBasicMaterial({
        color: darkerColor,
        transparent: true,
        opacity: 0.7
      });
      
      const mountain = new THREE.Mesh(geometry, material);
      mountain.position.z = -2;
      scene.add(mountain);
      
      // Add a softer foreground mountain
      const foreShape = new THREE.Shape();
      foreShape.moveTo(-width/2, -2);
      
      for (let i = 0; i <= segments; i++) {
        const x = -width/2 + (width * i / segments);
        const y = Math.sin(i/segments * Math.PI) * 0.5 + (Math.random() * 0.3) - 1;
        foreShape.lineTo(x, y);
      }
      
      foreShape.lineTo(width/2, -2);
      
      const foreGeometry = new THREE.ShapeGeometry(foreShape);
      const foreMaterial = new THREE.MeshBasicMaterial({
        color: mainColor,
        transparent: true,
        opacity: 0.5
      });
      
      const foreMountain = new THREE.Mesh(foreGeometry, foreMaterial);
      foreMountain.position.z = -1;
      scene.add(foreMountain);
    };
    
    createMiniMountain();
    
    // Add a gradient sky
    const skyGeometry = new THREE.PlaneGeometry(10, 6);
    const skyMaterial = new THREE.ShaderMaterial({
      transparent: true,
      uniforms: {
        color1: { value: mainColor },
        color2: { value: darkerColor },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color1;
        uniform vec3 color2;
        varying vec2 vUv;
        
        void main() {
          vec3 color = mix(color2, color1, vUv.y);
          gl_FragColor = vec4(color, 0.4); // Subtle transparency
        }
      `
    });
    
    const sky = new THREE.Mesh(skyGeometry, skyMaterial);
    sky.position.z = -3;
    scene.add(sky);
    
    // Add some stars or floaty particles
    const particlesCount = 10;
    const particlesGeometry = new THREE.BufferGeometry();
    const particlesPositions = new Float32Array(particlesCount * 3);
    
    for (let i = 0; i < particlesCount; i++) {
      particlesPositions[i * 3] = (Math.random() - 0.5) * 8;
      particlesPositions[i * 3 + 1] = (Math.random() - 0.5) * 4;
      particlesPositions[i * 3 + 2] = (Math.random() - 0.5) * 2 - 1;
    }
    
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(particlesPositions, 3));
    
    // Create particle texture
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const context = canvas.getContext('2d');
    
    if (context) {
      const gradient = context.createRadialGradient(16, 16, 0, 16, 16, 16);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
      gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.5)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      context.fillStyle = gradient;
      context.fillRect(0, 0, 32, 32);
    }
    
    const particlesTexture = new THREE.CanvasTexture(canvas);
    
    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.15,
      map: particlesTexture,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0.7
    });
    
    const particles = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particles);
    
    // Optionally add a celestial body (sun/moon) based on theme
    const celestialSize = 0.4;
    const celestialGeometry = new THREE.CircleGeometry(celestialSize, 32);
    const celestialMaterial = new THREE.MeshBasicMaterial({
      color: theme === 'light' ? 0xFDB813 : 0xE1E1E1,
      transparent: true,
      opacity: 0.8
    });
    
    const celestial = new THREE.Mesh(celestialGeometry, celestialMaterial);
    celestial.position.set(
      theme === 'light' ? 2 : -2, // Right for sun, left for moon
      1.5,
      -2
    );
    scene.add(celestial);
    
    camera.position.z = 3;
    
    // Animation loop
    const clock = new THREE.Clock();
    
    const animate = () => {
      const animationId = requestAnimationFrame(animate);
      const time = clock.getElapsedTime();
      
      // Very subtle animation for particles
      for (let i = 0; i < particlesCount; i++) {
        const i3 = i * 3;
        particlesPositions[i3] += Math.sin(time + i) * 0.0005;
        particlesPositions[i3 + 1] += Math.cos(time + i) * 0.0005;
      }
      
      particles.geometry.attributes.position.needsUpdate = true;
      
      // Subtle celestial body pulsing
      const pulseAmount = Math.sin(time * 0.5) * 0.03 + 1;
      celestial.scale.set(pulseAmount, pulseAmount, 1);
      
      renderer.render(scene, camera);
      return animationId;
    };
    
    const animationId = animate();
    
    // Handle resize
    const handleResize = () => {
      if (currentMount) {
        const width = currentMount.clientWidth;
        const height = currentMount.clientHeight;
        renderer.setSize(width, height);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
      
      if (currentMount && currentMount.contains(renderer.domElement)) {
        currentMount.removeChild(renderer.domElement);
      }
      
      // Dispose resources
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh || object instanceof THREE.Points) {
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(material => material.dispose());
            } else {
              object.material.dispose();
            }
          }
        }
      });
      
      renderer.dispose();
    };
  }, [color, theme]); // Add theme as dependency
  
  return <div ref={mountRef} className="w-full h-full rounded-xl overflow-hidden" />;
};

export default CardBackground;